import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { canonicalFor, getRouteMeta, isKnownRoute, SITE_NAME } from '../../lib/seo'

/** Set or update a <meta> tag by name or property attribute. */
function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/** Set or update the <link rel="canonical"> tag. */
function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Keeps the document title, description, canonical, and social tags in sync with
 * the current route. Rendered once near the top of the app.
 */
export function Seo() {
  const { pathname } = useLocation()

  useEffect(() => {
    const { title, description } = getRouteMeta(pathname)
    const canonical = canonicalFor(pathname)

    document.title = title
    setMeta('name', 'description', description)
    setCanonical(canonical)

    // Don't let unknown (404) URLs get indexed; keep real routes indexable.
    setMeta('name', 'robots', isKnownRoute(pathname) ? 'index, follow' : 'noindex, follow')

    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', canonical)
    setMeta('property', 'og:site_name', SITE_NAME)

    setMeta('name', 'twitter:title', title)
    setMeta('name', 'twitter:description', description)
  }, [pathname])

  return null
}
